/* Shared look for anchored popups: select and combobox lists, menus and popovers.
   Portaled parts get no z-index; DOM order stacks them (see src/styles/README.md). */
.positioner {
  outline: 0;
}

.popup {
  box-sizing: border-box;
  min-inline-size: max(var(--anchor-width, 0), var(--menu-min));
  max-inline-size: min(var(--available-width, 100vw), var(--popover-max));
  max-block-size: var(--available-height);
  padding: var(--space-1);
  overflow-y: auto;
  overscroll-behavior: contain;
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface-raised);
  color: var(--color-text);
  box-shadow: var(--shadow-popover);
  outline: 0;
  transform-origin: var(--transform-origin);
  transition:
    opacity var(--duration-normal) var(--easing-standard),
    transform var(--duration-normal) var(--easing-standard);
}

.popup[data-starting-style],
.popup[data-ending-style] {
  opacity: 0;
  transform: translateY(calc(var(--motion-distance) * -1)) scale(var(--motion-scale));
}

.popup[data-side="none"] {
  transition: none;
}

.list {
  display: flex;
  flex-direction: column;
  outline: 0;
}

/* A select aligned with its trigger caps the list at the popup's height and scrolls it, so
   rows must not shrink (two-line rows would overlap the next one). */
.list > * {
  flex: none;
}

.item {
  display: grid;
  grid-template-columns: var(--icon-sm) 1fr auto;
  gap: var(--space-2);
  align-items: center;
  min-block-size: var(--control-md);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-md);
  color: var(--color-text);
  font-size: var(--text-md);
  line-height: var(--leading-tight);
  text-decoration: none;
  cursor: default;
  user-select: none;
  outline: 0;
}

.item[data-highlighted] {
  background: var(--color-surface-hover);
  color: var(--color-text);
}

.item[data-disabled] {
  color: var(--color-text-subtle);
}

.item[data-danger] {
  color: var(--color-danger);
}

.itemIndicator {
  display: inline-flex;
  grid-column: 1;
  color: var(--color-accent);
}

.itemText {
  grid-column: 2;
  min-inline-size: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.itemDetail {
  grid-column: 2;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.itemHint {
  grid-column: 3;
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
}

.groupLabel {
  padding: var(--space-2) var(--space-2) var(--space-1);
  color: var(--color-text-muted);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
}

.separator {
  block-size: var(--border-width);
  margin: var(--space-1) calc(var(--space-1) * -1);
  background: var(--color-border);
}

.status {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-2);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

/* Select trigger and combobox input reuse the field frame from Field.module.css. */
.trigger {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  justify-content: space-between;
  inline-size: 100%;
  min-block-size: var(--control-md);
  padding: 0 var(--space-2);
  border: var(--border-width) solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: var(--text-md);
  text-align: start;
  cursor: default;
}

.trigger[data-size="sm"] {
  min-block-size: var(--control-sm);
  font-size: var(--text-sm);
}

@media (hover: hover) and (pointer: fine) {
  .trigger:hover:not([data-disabled]) {
    background: var(--color-surface-hover);
  }
}

.trigger[data-popup-open] {
  border-color: var(--color-accent);
}

.trigger[data-invalid] {
  border-color: var(--color-danger);
}

.trigger[data-disabled] {
  border-color: var(--color-border);
  background: var(--color-surface-sunken);
  color: var(--color-text-subtle);
}

.value {
  min-inline-size: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.value[data-placeholder] {
  color: var(--color-text-subtle);
}

.triggerIcon {
  display: inline-flex;
  color: var(--color-text-muted);
}
